import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { JsonLd } from "@/components/JsonLd";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { RangpurAreaNav } from "@/components/RangpurAreaNav";
import { CityServiceView } from "@/components/CityServiceView";
import { cityServiceHead } from "@/lib/city-service-head";
import { AreaHero } from "@/components/rangpur-area/AreaHero";
import { AreaInsights } from "@/components/rangpur-area/AreaInsights";
import {
  AreaFaq,
  AreaLocalContext,
  AreaMap,
  AreaPlaybook,
  AreaRecommendations,
} from "@/components/rangpur-area/AreaSections";
import {
  AreaAllAreas,
  AreaCta,
  AreaNearest,
  AreaRelatedServices,
} from "@/components/rangpur-area/AreaLinks";
import { RANGPUR_AREAS, loadRangpurArea, type RangpurArea } from "@/content/rangpur-areas";
import { SERVICES, type Service } from "@/content/services";
import { isProgrammableService } from "@/content/city-services";
import { LOCATIONS } from "@/content/locations";
import { buildRangpurArea } from "@/lib/rangpur-area";
import { buildSeo, breadcrumbSchema } from "@/lib/seo";

/**
 * Rangpur neighbourhood pages at /seo-services/rangpur/$area. Sections live in
 * src/components/rangpur-area; this file owns routing, loading and metadata.
 */

const RANGPUR = LOCATIONS.find((l) => l.slug === "rangpur")!;

export const Route = createFileRoute("/seo-services/rangpur/$area")({
  // Canonicalize: lowercase the slug and drop any trailing slash / query
  // params before matching, so duplicate URL shapes (uppercased,
  // ?utm=..., trailing /) all 301 to the single canonical area URL.
  beforeLoad: ({ params, location }) => {
    const raw = params.area;
    const canonical = raw.trim().toLowerCase().replace(/\/+$/, "");
    const hasQuery = location.searchStr && location.searchStr.length > 0;
    if (canonical !== raw || hasQuery) {
      throw redirect({
        to: "/seo-services/rangpur/$area",
        params: { area: canonical },
        replace: true,
      });
    }
  },
  // This static-segment route shadows /seo-services/$location/$service for
  // Rangpur, so it must serve both shapes: neighbourhood areas AND the
  // programmatic city x service pages every other city gets.
  loader: async ({
    params,
  }): Promise<{ area?: RangpurArea; indexable?: boolean; svc?: Service }> => {
    const meta = RANGPUR_AREAS.find((a) => a.slug === params.area);
    if (meta) {
      // Heavy area copy is loaded on demand, keeping it out of the entry chunk.
      const area = await loadRangpurArea(meta.slug);
      if (area) return { area, indexable: meta.indexable };
    }
    if (isProgrammableService(params.area)) {
      const svc = SERVICES.find((s) => s.slug === params.area);
      if (svc) return { svc };
    }
    throw notFound();
  },
  head: ({ loaderData }) => {
    if (loaderData?.svc) return cityServiceHead(RANGPUR, loaderData.svc);
    const a = loaderData?.area;
    if (!a) return { meta: [{ title: "Area not found" }] };
    const indexable = loaderData?.indexable ?? false;
    // When the page is enriched, lean on area-specific terms in title +
    // description so two area pages don't share the same boilerplate.
    const primaryBiz = a.businesses[0] ?? "local businesses";
    const description = indexable
      ? `${a.searchBehavior?.split(".")[0]}. Local SEO, GBP optimization, and ${primaryBiz.toLowerCase()}-focused strategy for ${a.name}, Rangpur.`.slice(
          0,
          300,
        )
      : `Local SEO, Google Business Profile, and map-pack optimization for businesses near ${a.name} in Rangpur, Bangladesh.`;
    const title = indexable
      ? `${a.name} SEO Expert, Rangpur — ${primaryBiz} | Freelancer Tamal`
      : `SEO near ${a.name}, Rangpur | Freelancer Tamal`;
    const keywordTail = a.localKeywords?.length
      ? a.localKeywords.slice(0, 5).join(", ")
      : a.aliases.join(", ");
    return buildSeo({
      title,
      description,
      path: `/seo-services/rangpur/${a.slug}`,
      keywords: `SEO ${a.name} Rangpur, local SEO ${a.name}, ${primaryBiz} SEO Rangpur, ${keywordTail}`,
      ogEyebrow: `Rangpur · ${a.name}`,
      ogTitle: indexable ? `${a.name} — ${primaryBiz} SEO` : `SEO Expert near ${a.name}`,
      noindex: !indexable,
    });
  },
  component: AreaPage,
  notFoundComponent: () => (
    <div className="container-x py-32 text-center">
      <h1 className="font-display text-3xl">Area not found</h1>
      <Link
        to="/seo-services/$location"
        params={{ location: "rangpur" }}
        className="mt-6 inline-flex text-primary"
      >
        ← All Rangpur areas
      </Link>
    </div>
  ),
});

function AreaPage() {
  const data = Route.useLoaderData();
  if (data.svc) return <CityServiceView loc={RANGPUR} svc={data.svc} />;
  const a = data.area!;

  const { businessSchema, mapEmbedSrc, mapsLink, others, neighbors, relatedServices, faqs } =
    buildRangpurArea(a);
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "SEO Locations", path: "/seo-locations" },
    { name: "Rangpur", path: "/seo-services/rangpur" },
    { name: a.name, path: `/seo-services/rangpur/${a.slug}` },
  ];

  return (
    <>
      <Breadcrumbs items={crumbs} />
      <RangpurAreaNav current={a} />
      <JsonLd data={businessSchema} />
      <JsonLd data={breadcrumbSchema(crumbs)} />
      <AreaHero a={a} />
      <AreaInsights a={a} />
      <AreaRecommendations a={a} relatedServices={relatedServices} neighbors={neighbors} />
      <AreaLocalContext a={a} />
      <AreaPlaybook a={a} />
      <AreaFaq a={a} faqs={faqs} />
      <AreaMap a={a} city={RANGPUR} mapEmbedSrc={mapEmbedSrc} mapsLink={mapsLink} />
      <AreaNearest a={a} neighbors={neighbors} />
      <AreaRelatedServices a={a} relatedServices={relatedServices} />
      <AreaAllAreas others={others} />
      <AreaCta a={a} />
    </>
  );
}
